/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import type { ReactNode } from 'react';

import {
  Animation,
  Button,
  type ButtonProps,
  Icon,
  Link,
  IconName,
  Typography,
} from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import type { NavItemLink } from '../../../constants/links';
import { UNIVERSAL_TOPBAR_HEIGHT } from '../../../constants';
import { useUniversalNavContext } from '../../../hooks';

type Props = {
  /**
   * An aria label for the link element.
   */
  ariaLabel?: string;
  buttonSize?: ButtonProps['size'];
  /**
   * Content to render within the link.
   */
  children: ReactNode;
  /**
   * Renders this link as a button.
   */
  isButton?: boolean;

  icon?: IconName;

  /**
   *  The item to be rendered
   */
  navigationItem: NavItemLink;
  /**
   * Determines spacing styles if link is located
   * in the accounts menu vs. the main menu
   */
  position?: 'actions-menu';
  /**
   * Refers to the size of the topbar.
   */
  size?: 'small' | 'medium';
  /**
   * Optional click handler
   */
  onClick?: () => void;
  className?: string;
};

const Styles = labelStyles('InAppTopbarLink', {
  root: [
    css({
      paddingRight: 18,
      display: 'flex',
      alignItems: 'center',
      color: 'var(--zds-text-default, #413735)',
      font: `var(--zds-body-1, 500 14px/24px ${Typography.base.fontFamily})`,
      '&[data-position="actions-menu"]': {
        paddingRight: 0,
      },
    }),
  ],

  link: [
    css({
      color: 'inherit',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: UNIVERSAL_TOPBAR_HEIGHT,
      lineHeight: `${UNIVERSAL_TOPBAR_HEIGHT}px`,
      font: Typography.base.fontFamily,
      cursor: 'pointer',
      textDecoration: 'none',
      position: 'relative',

      '&:visited': {
        color: 'inherit',
      },

      '&:focus': {
        outline: 'none',
      },
      '&:focus-visible > span': {
        '&::after': {
          border: 'var(--zds-border-focus, 2px solid #695BE8)',
          top: 6,
          right: '-4px',
          bottom: 6,
          left: '-3px',
        },
      },
      // Focus backgroundColor defined on `linkInner`
      '&:focus-within > span, &:hover > span': {
        '&::before': {
          backgroundColor: 'var(--zds-background-stronger, #F5F3EB)',
        },
      },
    }),
  ],
  linkInner: (size: Props['size']) =>
    // https://stackoverflow.com/questions/55780450/why-pseudo-element-background-color-covered-its-parent-background-color-when-use
    css({
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--zds-space-4, 4px)',
      fontWeight: 500,
      transformStyle: 'preserve-3d',
      height: '100%',
      padding:
        size === 'medium'
          ? '0 var(--zds-space-12, 12px)'
          : '0 var(--zds-space-4, 4px)',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        top: 8,
        right: 0,
        bottom: 8,
        left: 0,
        border: '2px solid transparent',
        borderRadius: 4,
        transitionProperty: 'all',
        transitionTimingFunction: `${Animation.transitionTimingFunction}`,
        transitionDuration: '200ms',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 10,
        right: 'var(--zds-space-0, 0px)',
        bottom: 20,
        left: 'var(--zds-space-0, 0px)',
        transform: 'translateZ(-1px)',
        borderRadius: 'var(--zds-radius-small, 3px)',
        height: 36,
      },
    }),
});

/**
 * @private
 * Wrapper that renders either a `Link` or CTA button.
 */
export const InAppTopbarLink = ({
  ariaLabel,
  children,
  isButton = false,
  navigationItem,
  position,
  icon,
  onClick,
  size = 'medium',
  buttonSize = 'xsmall',
  className,
}: Props) => {
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (eventType: string) => {
    let eventMessage: string = '';
    if (eventType === 'click') {
      eventMessage = `UniversalLayout DynamicTopBar: ${navigationItem.href}`;
    } else {
      eventMessage = `UniversalLayout DynamicTopBar: ${navigationItem.href}`;
    }
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: eventMessage,
    });
  };

  return (
    <span
      css={Styles.root}
      data-position={position}
      data-testid="topbar-link"
      className={className}
      data-zds
    >
      {isButton ? (
        <Button
          href={getFullyQualifiedURL(navigationItem.href)}
          color={navigationItem.color}
          size={buttonSize}
          {...(onClick && { onClick })}
        >
          {children}
        </Button>
      ) : (
        <Link
          color={null}
          css={Styles.link}
          data-selected={false}
          href={getFullyQualifiedURL(navigationItem.href)}
          target={navigationItem.target}
          onClick={() => {
            handleEvent('click');
          }}
          onMouseEnter={() => handleEvent('hover')}
          ariaLabel={ariaLabel}
        >
          <span css={Styles.linkInner(size)} data-zds>
            {icon && <Icon name={icon} size={22} />}
            {children}
          </span>
        </Link>
      )}
    </span>
  );
};

export default InAppTopbarLink;
